import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../../../services/api-base';
import type {
  PropertyEntity,
  PropertyPageResponse,
  PropertyRequest,
  PropertyBulkUploadRequest,
  PropertyDTO,
  PropertyErrorFormData,
  OrderErrorFormData,
  OrderBulkFixResponse,
} from '../../../types/property.types';

// ---------------------------------------------------------------------------
// Request param types
// ---------------------------------------------------------------------------

interface PaginatedListParams {
  page: number;
  size: number;
  filterQuery: string;
  sortQuery: string;
}

interface DropdownParams {
  endpoint: string;
  serviceType: 'property' | 'entity';
}

// ---------------------------------------------------------------------------
// API slice
// ---------------------------------------------------------------------------

export const propertyApi = createApi({
  reducerPath: 'propertyApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['PropertyList', 'Property', 'BulkUpload'],
  endpoints: (builder) => ({
    // -----------------------------------------------------------------------
    // Property listing
    // -----------------------------------------------------------------------

    getProperties: builder.query<PropertyPageResponse, PaginatedListParams>({
      query: ({ page, size, filterQuery, sortQuery }) => {
        const params = `page=${page}&size=${size}${filterQuery}${sortQuery ? `&${sortQuery}` : ''}`;
        return `/property-service/api/v1/properties?${params}`;
      },
      providesTags: ['PropertyList'],
    }),

    /**
     * Single-property lookup by UUID. The backend has no dedicated
     * GET /properties/{uuid} endpoint, so we wrap the list endpoint
     * with a propertyId filter and unwrap the single result.
     *
     * Returns undefined when the list response has no content — consumers
     * treat that as the 404 / cross-org / deleted case. Real network or
     * auth errors still propagate through RTK Query's error channel.
     */
    getPropertyById: builder.query<PropertyEntity | undefined, string>({
      query: (propertyId) =>
        `/property-service/api/v1/properties?propertyId=${propertyId}&size=1`,
      transformResponse: (response: PropertyPageResponse): PropertyEntity | undefined =>
        response.content?.[0],
      providesTags: (_result, _error, propertyId) => [
        { type: 'Property', id: propertyId },
      ],
    }),

    getPropertyStatuses: builder.query<string[], void>({
      query: () => '/property-service/api/v1/properties/propertyStatus',
    }),

    // -----------------------------------------------------------------------
    // Property CRUD
    // -----------------------------------------------------------------------

    createProperty: builder.mutation<PropertyEntity, PropertyRequest>({
      query: (body) => ({
        url: '/property-service/api/v1/properties',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PropertyList'],
    }),

    updateProperty: builder.mutation<
      PropertyEntity,
      { propId: string; body: PropertyRequest }
    >({
      query: ({ propId, body }) => ({
        url: `/property-service/api/v1/properties/${propId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { propId }) => [
        'PropertyList',
        { type: 'Property', id: propId },
      ],
    }),

    // -----------------------------------------------------------------------
    // Dropdown data
    // -----------------------------------------------------------------------

    getDropdownData: builder.query<string[], DropdownParams>({
      query: ({ endpoint, serviceType }) => {
        const base =
          serviceType === 'property'
            ? '/property-service/api/v1/properties'
            : '/entity-service/api/v1/entities';
        return `${base}/${endpoint}`;
      },
    }),

    // -----------------------------------------------------------------------
    // Bulk import — properties
    // -----------------------------------------------------------------------

    getBulkUploadRequests: builder.query<PropertyBulkUploadRequest[], void>({
      query: () => '/property-service/api/v1/properties/bulk/requests',
      providesTags: ['BulkUpload'],
    }),

    getBulkUploadFileErrors: builder.query<PropertyDTO[], string>({
      query: (fileId) =>
        `/property-service/api/v1/properties/bulk/requests/${fileId}`,
    }),

    fixBulkPropertyErrors: builder.mutation<
      PropertyDTO[],
      PropertyErrorFormData[]
    >({
      query: (body) => ({
        url: '/property-service/api/v1/properties/bulk/list',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BulkUpload'],
    }),

    // -----------------------------------------------------------------------
    // Bulk import — orders (shared: FixErrorImports + BulkImportPage)
    // -----------------------------------------------------------------------

    getOrderBulkUploadRequests: builder.query<
      PropertyBulkUploadRequest[],
      void
    >({
      query: () => '/order-service/api/v1/orders/bulk/requests',
    }),

    getOrderBulkUploadFileErrors: builder.query<PropertyDTO[], string>({
      query: (fileId) =>
        `/order-service/api/v1/orders/bulk/requests/${fileId}`,
    }),

    fixBulkOrderErrors: builder.mutation<
      OrderBulkFixResponse,
      OrderErrorFormData[]
    >({
      query: (body) => ({
        url: '/order-service/api/v1/orders/bulkupload/fix',
        method: 'POST',
        body,
      }),
    }),

    // -----------------------------------------------------------------------
    // Notifications (edit/cancel order requests from PropertyView)
    // -----------------------------------------------------------------------

    sendNotification: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({
        url: '/notification-service/api/v1/notifications',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useLazyGetPropertiesQuery,
  useGetPropertyByIdQuery,
  useGetPropertyStatusesQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useGetDropdownDataQuery,
  useGetBulkUploadRequestsQuery,
  useGetBulkUploadFileErrorsQuery,
  useLazyGetBulkUploadFileErrorsQuery,
  useFixBulkPropertyErrorsMutation,
  useGetOrderBulkUploadRequestsQuery,
  useGetOrderBulkUploadFileErrorsQuery,
  useLazyGetOrderBulkUploadFileErrorsQuery,
  useFixBulkOrderErrorsMutation,
  useSendNotificationMutation,
} = propertyApi;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map a string array from the dropdown API to select options */
export const mapDropDownValues = (
  data: string[],
): Array<{ value: string; label: string }> =>
  data.map((v) => ({ value: v, label: v }));
